/**
 * SelfEmployedForm — Batch #14.330 : entrées du travailleur autonome (revenu brut estimé, dépenses estimées, province d'emploi,
 * âge pour le RRQ/RPC, AE facultative) + aide « je ne sais pas combien je ferai » (an dernier × croissance).
 */
import { useState } from "react";
import { Field, inputCls } from "@/pages/admin/comptabilite/_shared";

export const PROVINCES = [["QC", "Québec"], ["ON", "Ontario"], ["NB", "Nouveau-Brunswick"], ["NS", "Nouvelle-Écosse"], ["PE", "Île-du-Prince-Édouard"], ["NL", "Terre-Neuve-et-Labrador"],
  ["MB", "Manitoba"], ["SK", "Saskatchewan"], ["AB", "Alberta"], ["BC", "Colombie-Britannique"], ["YT", "Yukon"], ["NT", "Territoires du Nord-Ouest"], ["NU", "Nunavut"]];

const num = (v) => { const n = parseFloat(String(v ?? "").replace(/\s/g, "").replace(",", ".")); return Number.isFinite(n) ? n : 0; };
const fmt = (n) => n.toLocaleString("fr-CA", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

/** Estimateur : montant de l'an dernier × (1 + croissance %) → remplit le champ cible. */
function Projector({ label, onApply, testId }) {
  const [prior, setPrior] = useState("");
  const [pct, setPct] = useState("10");
  const projected = Math.max(0, num(prior) * (1 + num(pct) / 100));
  return (
    <div className="rounded-lg bg-slate-50 border border-dashed p-2 text-xs space-y-1" data-testid={`${testId}-projector`}>
      <div className="text-[11px] text-slate-600">Vous ne savez pas ? {label} de l'an dernier × croissance</div>
      <div className="flex items-center gap-1">
        <input type="text" inputMode="decimal" className={`${inputCls} font-mono text-right`} value={prior} onChange={(e) => setPrior(e.target.value)} placeholder="An dernier" data-testid={`${testId}-prior`} />
        <span className="text-slate-500">×</span>
        <input type="text" inputMode="decimal" className={`${inputCls} w-16 font-mono text-right`} value={pct} onChange={(e) => setPct(e.target.value)} data-testid={`${testId}-growth`} />
        <span className="text-slate-500">%</span>
        <button type="button" onClick={() => onApply(String(Math.round(projected)))} disabled={!num(prior)} className="px-2 py-1 rounded-md bg-violet-600 text-white font-semibold disabled:opacity-40 whitespace-nowrap" data-testid={`${testId}-apply`}>→ {fmt(projected)} $</button>
      </div>
    </div>
  );
}

export default function SelfEmployedForm({ v, set }) {
  const age = num(v.age);
  const qc = v.province === "QC";
  const optionalAge = age >= 65 && age <= (qc ? 72 : 69);
  return (
    <div className="bg-white border rounded-xl p-4 space-y-3" data-testid="inst-se-form">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Province d'emploi" hint={qc ? "RRQ · RQAP · abattement fédéral 16,5 %" : "RPC · AE au taux fédéral · impôt provincial perçu par l'ARC"}>
          <select className={inputCls} value={v.province} onChange={(e) => set("province", e.target.value)} data-testid="inst-se-province">{PROVINCES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}</select>
        </Field>
        <Field label="Âge au 31 décembre" hint={qc ? "RRQ : 18 à 72 ans (facultatif de 65 à 72)" : "RPC : 18 à 69 ans (facultatif de 65 à 69)"}>
          <input type="number" min="0" max="110" className={inputCls} value={v.age} onChange={(e) => set("age", e.target.value)} placeholder="ex. 42" data-testid="inst-se-age" />
        </Field>
        {optionalAge ? (
          <Field label={`Cotise encore au ${qc ? "RRQ" : "RPC"} ?`} hint="65 ans et plus : cotisation facultative">
            <select className={inputCls} value={v.pension_optional_contribute ? "1" : "0"} onChange={(e) => set("pension_optional_contribute", e.target.value === "1")} data-testid="inst-se-pension-opt"><option value="1">Oui, continue de cotiser</option><option value="0">Non, a cessé de cotiser</option></select>
          </Field>
        ) : (
          <Field label="Autres revenus (facultatif)" hint="Emploi, pension… — pour le palier d'imposition">
            <input type="text" inputMode="decimal" className={`${inputCls} font-mono text-right`} value={v.other_income} onChange={(e) => set("other_income", e.target.value)} placeholder="0" data-testid="inst-se-other" />
          </Field>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-2">
          <Field label="Revenu brut estimé (année)"><input type="text" inputMode="decimal" className={`${inputCls} font-mono text-right text-base`} value={v.revenue} onChange={(e) => set("revenue", e.target.value)} placeholder="0" data-testid="inst-se-revenue" /></Field>
          <Projector label="Revenu" onApply={(x) => set("revenue", x)} testId="inst-se-revenue" />
        </div>
        <div className="space-y-2">
          <Field label="Dépenses estimées (année)"><input type="text" inputMode="decimal" className={`${inputCls} font-mono text-right text-base`} value={v.expenses} onChange={(e) => set("expenses", e.target.value)} placeholder="0" data-testid="inst-se-expenses" /></Field>
          <Projector label="Dépenses" onApply={(x) => set("expenses", x)} testId="inst-se-expenses" />
        </div>
      </div>
      <div className="flex items-center justify-between flex-wrap gap-2 text-xs">
        <div className="font-semibold text-slate-700" data-testid="inst-se-net">Revenu net d'entreprise estimé : <span className="font-mono text-base">{fmt(Math.max(0, num(v.revenue) - num(v.expenses)))} $</span></div>
        <label className="inline-flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={!!v.ei_opt_in} onChange={(e) => set("ei_opt_in", e.target.checked)} data-testid="inst-se-ei" /><span>Inscrit à l'assurance-emploi des travailleurs autonomes (facultatif)</span></label>
      </div>
    </div>
  );
}
